import { ChangeEventHandler, FocusEventHandler, forwardRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';

import { Textarea } from '../Input/Textarea/Textarea';

type SchemableFormTextareaProps = Omit<Parameters<typeof Textarea>[0], 'errors' | 'hasErrors'> & {
  error: WebEDIError | undefined;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  totalNumberOfErrors: number;
};
export const SchemableFormTextarea = forwardRef<HTMLTextAreaElement, SchemableFormTextareaProps>(
  (
    {
      error,
      totalNumberOfErrors,
      onBlur,
      onShowFieldErrorModal,
      description,
      variant,
      valueDescription,
      fieldState,
      onChange,
      name,
      value,
      ...otherProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isValidating = useWebEDIStore((state) => state.isValidating);
    const { ErrorModalComponent, useErrorModal } = useErrorContext();
    const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
    const onTextareaFocus = useCallback(() => {
      setShowErrorModal(true);
    }, [setShowErrorModal]);

    const onTextareaBlur: FocusEventHandler<HTMLTextAreaElement> = useCallback(
      (e) => {
        onBlur?.(e);
        setShowErrorModal(false);
      },
      [onBlur, setShowErrorModal],
    );

    const handleOnChange: ChangeEventHandler<HTMLTextAreaElement> = useCallback(
      (event) => {
        onChange?.(event);
      },
      [onChange],
    );

    useEffect(() => {
      if (error && showErrorModal) {
        onShowFieldErrorModal?.(true);
      }
    }, [error, onShowFieldErrorModal, showErrorModal]);

    if (!name) {
      return null;
    }

    const isLargeVariant = variant === 'large';
    const inlineErrors =
      isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;

    return (
      <div>
        <Textarea
          {...otherProps}
          value={value ?? ''}
          placeholder={!value && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined}
          onChange={handleOnChange}
          ref={ref}
          wrapperRef={isLargeVariant ? undefined : refs.setReference}
          description={isLargeVariant ? description : undefined}
          errors={inlineErrors}
          fieldState={fieldState}
          onFocus={isLargeVariant ? undefined : onTextareaFocus}
          onBlur={isLargeVariant ? onBlur : onTextareaBlur}
          name={name}
          data-field={name}
          data-1p-ignore={true}
          {...(isLargeVariant ? { variant, valueDescription } : { variant: 'default' })}
        />

        {!isLargeVariant && showErrorModal && ErrorModalComponent && (
          <ErrorModalComponent
            viewMode={false}
            error={error}
            ref={refs.setFloating}
            style={floatingStyles}
            numberOfErrors={totalNumberOfErrors}
            isValidating={isValidating}
            fieldDescription={description}
            isPendingValidation={fieldState === 'pending-validation'}
          />
        )}
      </div>
    );
  },
);

SchemableFormTextarea.displayName = 'SchemableFormTextarea';
